import { DateTime } from 'luxon';
import { forwardedViewOptions } from '../../groupEvents';
import { toPrintableLayout } from '../../printableConfigAdapter';
import type {
  MeasurementResult,
  PrintableEvent,
  PrintableTemplate,
} from '../../types';
import { expandAndGroupByMonth } from './group';
import type { GroupData, Month, MonthData, PageData } from './types';
import { toMonthKey } from './util.lib';

const GRID_ROW_GAP = 0;
const FIRST_DAY_OF_WEEK = 7;

type DayOverflow = {
  date: GroupData['date'];
  groupIndex: number;
  pages: PrintableEvent[][];
};

type BoxCountLookup = Record<number, number>;

const setBoxCounts = (pages: PageData[]): PageData[] => {
  const boxCountByGroup = pages.reduce<BoxCountLookup>(
    (lookup, page) =>
      page.months.reduce(
        (acc, monthData) =>
          monthData.boxes.reduce((innerAcc, box) => {
            if (box.events.length === 0) return innerAcc;
            return { ...innerAcc, [box.groupIndex]: box.boxIndexForGroup + 1 };
          }, acc),
        lookup
      ),
    {}
  );

  return pages.map((page): PageData => ({
    ...page,
    months: page.months.map((monthData): MonthData => ({
      ...monthData,
      boxes: monthData.boxes.map((box): GroupData => {
        const boxCountForGroup =
          box.events.length === 0 ? 0 : boxCountByGroup[box.groupIndex];
        return { ...box, boxCountForGroup };
      }),
    })),
  }));
};

const calcWeekRowCount = (month: number, year: number): number => {
  const firstOfMonth = DateTime.fromObject({ year, month, day: 1 });
  const daysInMonth = firstOfMonth.daysInMonth!;
  const startWeekday = firstOfMonth.weekday;
  const daysIntoFirstWeek = (startWeekday - FIRST_DAY_OF_WEEK + 7) % 7;
  const totalDays = daysIntoFirstWeek + daysInMonth;
  return Math.ceil(totalDays / 7);
};

const assignDayToRow = (day: number, month: number, year: number): number => {
  const firstOfMonth = DateTime.fromObject({ year, month, day: 1 });
  const startWeekday = firstOfMonth.weekday;
  const daysIntoFirstWeek = (startWeekday - FIRST_DAY_OF_WEEK + 7) % 7;
  return Math.floor((daysIntoFirstWeek + day - 1) / 7);
};

const calcDayEventHeight = (
  box: GroupData,
  measurements: MeasurementResult
): number => {
  let total = 0;
  for (const event of box.events) {
    const eventKey = `event-${event.id}-${box.date.toISODate()}`;
    const m = measurements[eventKey];
    if (!m) {
      throw new Error(`Unable to find measurement for event="${eventKey}"`);
    }
    total += m.height + m.marginTop + m.marginBottom;
  }
  return total;
};

const calcRowRequiredHeights = (
  boxes: GroupData[],
  measurements: MeasurementResult,
  month: number,
  year: number,
  rowCount: number
): number[] => {
  const rowHeights = new Array<number>(rowCount).fill(0);
  for (const box of boxes) {
    if (box.events.length === 0) continue;
    const row = assignDayToRow(box.date.day, month, year);
    const dayHeight = calcDayEventHeight(box, measurements);
    if (dayHeight > rowHeights[row]) {
      rowHeights[row] = dayHeight;
    }
  }
  return rowHeights;
};

const calcDayHeaderAndPadding = (
  measurements: MeasurementResult,
  monthKey: string
): number => {
  const dayHeaderM = measurements[`day-header-${monthKey}-1`];
  if (!dayHeaderM) {
    throw new Error(
      `Unable to find day header measurement for month="${monthKey}"`
    );
  }
  const contentM = measurements[`day-content-${monthKey}-1`];
  if (!contentM) {
    throw new Error(
      `Unable to find day content measurement for month="${monthKey}"`
    );
  }
  const contentPad =
    contentM.paddingTop +
    contentM.paddingBottom +
    contentM.marginTop +
    contentM.marginBottom;
  return dayHeaderM.height + contentPad;
};

const calcGridAvailableForRows = (
  measurements: MeasurementResult,
  monthKey: string,
  monthIndex: number
): number => {
  const gridM = measurements[`grid-${monthIndex}`];
  if (!gridM) {
    throw new Error(
      `Unable to find grid measurement for monthIndex="${monthIndex}"`
    );
  }
  const headerRowM = measurements[`header-row-${monthKey}`];
  if (!headerRowM) {
    throw new Error(
      `Unable to find header-row measurement for month="${monthKey}"`
    );
  }
  return gridM.contentHeight - headerRowM.height;
};

const calcAvailableEventHeight = (
  gridAvailableForRows: number,
  rowsOnPage: number,
  dayHeaderAndPadding: number
): number => {
  const perRow =
    (gridAvailableForRows - (rowsOnPage - 1) * GRID_ROW_GAP) / rowsOnPage;
  return Math.max(perRow - dayHeaderAndPadding, 0);
};

const balanceRows = (totalRows: number, pages: number): number[] => {
  const base = Math.floor(totalRows / pages);
  const remainder = totalRows % pages;
  const distribution: number[] = [];
  for (let i = 0; i < pages; i++) {
    distribution.push(base + (i < remainder ? 1 : 0));
  }
  return distribution;
};

type RowSplit = { start: number; end: number }[];

const generateSplitVariants = (
  totalRows: number,
  pages: number
): RowSplit[] => {
  const base = balanceRows(totalRows, pages);
  const variants: RowSplit[] = [];

  const toSplit = (distribution: number[]): RowSplit => {
    const split: RowSplit = [];
    let cursor = 0;
    for (const count of distribution) {
      split.push({ start: cursor, end: cursor + count - 1 });
      cursor += count;
    }
    return split;
  };

  variants.push(toSplit(base));

  if (pages === 2 && base[0] !== base[1]) {
    variants.push(toSplit([base[1], base[0]]));
  }

  return variants;
};

const findOptimalSplit = (
  rowRequiredHeights: number[],
  gridAvailableForRows: number,
  dayHeaderAndPadding: number
): RowSplit | null => {
  const totalRows = rowRequiredHeights.length;

  for (let pages = 1; pages <= totalRows; pages++) {
    const variants = generateSplitVariants(totalRows, pages);

    for (const split of variants) {
      const fits = split.every((range) => {
        const rowsOnPage = range.end - range.start + 1;
        const availableEventHeight = calcAvailableEventHeight(
          gridAvailableForRows,
          rowsOnPage,
          dayHeaderAndPadding
        );
        for (let r = range.start; r <= range.end; r++) {
          if (rowRequiredHeights[r] > availableEventHeight) {
            return false;
          }
        }
        return true;
      });

      if (fits) return split;
    }
  }

  return null;
};

const splitDayEvents = (
  boxes: GroupData[],
  measurements: MeasurementResult,
  availableHeight: number
): DayOverflow[] => {
  return boxes.map((box): DayOverflow => {
    const pages: PrintableEvent[][] = [];
    let currentEvents: PrintableEvent[] = [];
    let contentHeight = 0;

    for (const event of box.events) {
      const eventKey = `event-${event.id}-${box.date.toISODate()}`;
      const eventMeasurement = measurements[eventKey];

      if (!eventMeasurement) {
        throw new Error(`Unable to find measurement for event="${eventKey}"`);
      }

      const eventHeight =
        eventMeasurement.height +
        eventMeasurement.marginTop +
        eventMeasurement.marginBottom;

      if (availableHeight >= contentHeight + eventHeight) {
        currentEvents.push(event);
        contentHeight += eventHeight;
        continue;
      }

      if (currentEvents.length > 0) {
        pages.push(currentEvents);
        currentEvents = [];
        contentHeight = 0;
      }

      currentEvents.push(event);
      contentHeight += eventHeight;
    }

    pages.push(currentEvents);

    return {
      date: box.date,
      groupIndex: box.groupIndex,
      pages,
    };
  });
};

const flattenOverflowToPages = (
  dayOverflows: DayOverflow[],
  month: Month,
  weekRowRange: { start: number; end: number }
): PageData[] => {
  const maxPages = Math.max(...dayOverflows.map((d) => d.pages.length));
  const pages: PageData[] = [];
  let boxIndex = 0;

  for (let pageLayer = 0; pageLayer < maxPages; pageLayer++) {
    const pageBoxes: GroupData[] = dayOverflows.map((day): GroupData => {
      const events = day.pages[pageLayer] ?? [];
      return {
        date: day.date,
        groupIndex: day.groupIndex,
        boxIndex: boxIndex++,
        boxIndexForGroup: pageLayer,
        boxCountForGroup: 0,
        events,
      };
    });

    pages.push({
      pageIndexForMonth: pageLayer,
      months: [{ month, boxes: pageBoxes, weekRowRange }],
    });
  }

  return pages;
};

export const paginate: PrintableTemplate<PageData>['paginate'] = ({
  experience,
  events,
  measurements,
  options,
}) => {
  const groupedData = expandAndGroupByMonth({
    experience,
    events,
    options: {
      layout: toPrintableLayout(experience.layoutId),
      includeEmptyGroups: true,
      expandFullyForDuration: 'month',
      ...forwardedViewOptions(options),
    },
  });

  if (groupedData.months.every((m) => m.boxes.length === 0)) {
    return [groupedData];
  }

  const allPages: PageData[] = [];

  for (let monthIdx = 0; monthIdx < groupedData.months.length; monthIdx++) {
    const monthData = groupedData.months[monthIdx];
    const { month, year } = monthData.month;
    const monthKey = toMonthKey(monthData.month);

    if (monthData.boxes.length === 0) {
      allPages.push({
        pageIndexForMonth: 0,
        months: [monthData],
      });
      continue;
    }

    const rowCount = calcWeekRowCount(month, year);
    const rowRequiredHeights = calcRowRequiredHeights(
      monthData.boxes,
      measurements,
      month,
      year,
      rowCount
    );

    const hasEvents = rowRequiredHeights.some((h) => h > 0);
    if (!hasEvents) {
      allPages.push({
        pageIndexForMonth: 0,
        months: [
          { ...monthData, weekRowRange: { start: 0, end: rowCount - 1 } },
        ],
      });
      continue;
    }

    const dayHeaderAndPadding = calcDayHeaderAndPadding(measurements, monthKey);
    const gridAvailableForRows = calcGridAvailableForRows(
      measurements,
      monthKey,
      monthIdx
    );

    const split = findOptimalSplit(
      rowRequiredHeights,
      gridAvailableForRows,
      dayHeaderAndPadding
    );

    if (split) {
      split.forEach((range, pageIdx) => {
        const boxesForPage = monthData.boxes.filter((box) => {
          const row = assignDayToRow(box.date.day, month, year);
          return row >= range.start && row <= range.end;
        });

        allPages.push({
          pageIndexForMonth: pageIdx,
          months: [
            {
              month: monthData.month,
              boxes: boxesForPage,
              weekRowRange: range,
            },
          ],
        });
      });
    } else {
      for (let row = 0; row < rowCount; row++) {
        const rowBoxes = monthData.boxes.filter(
          (box) => assignDayToRow(box.date.day, month, year) === row
        );

        const singleRowAvailableEventHeight = calcAvailableEventHeight(
          gridAvailableForRows,
          1,
          dayHeaderAndPadding
        );

        if (rowRequiredHeights[row] <= singleRowAvailableEventHeight) {
          allPages.push({
            pageIndexForMonth: allPages.length,
            months: [
              {
                month: monthData.month,
                boxes: rowBoxes,
                weekRowRange: { start: row, end: row },
              },
            ],
          });
        } else {
          const overflowPages = flattenOverflowToPages(
            splitDayEvents(
              rowBoxes,
              measurements,
              singleRowAvailableEventHeight
            ),
            monthData.month,
            { start: row, end: row }
          );
          allPages.push(...overflowPages);
        }
      }
    }
  }

  // Reassign pageIndexForMonth per month
  const pagesByMonth = new Map<string, PageData[]>();
  for (const page of allPages) {
    for (const m of page.months) {
      const key = toMonthKey(m.month);
      if (!pagesByMonth.has(key)) pagesByMonth.set(key, []);
      pagesByMonth.get(key)!.push(page);
    }
  }
  for (const pages of pagesByMonth.values()) {
    pages.forEach((page, i) => {
      page.pageIndexForMonth = i;
    });
  }

  // Update boxIndex to be continuous across all pages
  let boxIndex = 0;
  const pagesWithContinuousIndex = allPages.map((page): PageData => ({
    ...page,
    months: page.months.map((monthData): MonthData => ({
      ...monthData,
      boxes: monthData.boxes.map((box): GroupData => ({
        ...box,
        boxIndex: boxIndex++,
      })),
    })),
  }));

  return setBoxCounts(pagesWithContinuousIndex);
};
